import { Link } from "react-router-dom";
import { defineListView } from "@/base/list/defineListView";
import type { ListFilterOption, ListFilterSource } from "@/base/list/types";
import type { AuthedBlobRequester, AuthedRequester } from "@/lib/brokerAuth";
import { downloadAccountDocument } from "@/lib/brokerAccounts";
import { formatBondDate } from "@/lib/bondValueDisplay";
import { invoiceHref } from "@/lib/invoices";
import { statusBadgeClass } from "@/lib/listFormat";
import {
  BILLING_KINDS,
  billingKindLabel,
  listAccountBillingDocuments,
  paymentStatusDisplay,
  type BillingDocumentRow,
  type BillingDocumentsPage,
} from "@/lib/accountBillingList";
import { DocumentDownload } from "@/components/surety-account/DocumentDownload";

// BRK-S411, BRK-S445 — the Surety account's Billing tab as a BaseListView
// definition, embedded in the profile (BRK-S255: every list is one). One list
// over the account's billing documents — invoices, payment receipts,
// statements and credit memos — from the billing-documents read (PMS-S854),
// which PMS filters, searches and pages.
//
// URL keys of its own (`billYear`, `billKind`, `billRelated`, `billQ`,
// `billPage`), beside the profile's `?tab=`, Bond history's `bh*` and
// Documents' `doc*`.
//
// The October wireframe's seven columns with D5 applied: Document ID ·
// Document name · Document kind · Related to · Added · Payment status ·
// Download. NEVER rendered (D5, BRK-S187): a Pay or Pay now button, a payment
// type (ACH / Check / Wire), a payment method or reference.

const WRAP = "[overflow-wrap:anywhere]";
const CHIP =
  "inline-flex shrink-0 items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium";
const TONE = {
  danger: "bg-brk-danger/10 text-brk-danger",
  success: statusBadgeClass("approved"),
  neutral: statusBadgeClass("draft"),
} as const;

export function accountBillingListView({
  request,
  fetchBlob,
  accountId,
}: {
  request: AuthedRequester;
  /** The authenticated file read, for each row's Download. */
  fetchBlob: AuthedBlobRequester;
  accountId: string;
}) {
  // The year and related-to choices come from ONE read of the served facets.
  // A failed read is forgotten, so the next mount asks again.
  let facets: Promise<BillingDocumentsPage["facets"]> | null = null;
  function readFacets() {
    if (facets) return facets;
    const read = listAccountBillingDocuments(request, accountId, {
      limit: 1,
      offset: 0,
    }).then((page) => page.facets);
    facets = read;
    read.catch(() => {
      if (facets === read) facets = null;
    });
    return read;
  }

  const endpoint =
    `/api/broker/accounts/${accountId}/billing-documents` as const;
  const served = (
    pick: (f: BillingDocumentsPage["facets"]) => readonly ListFilterOption[],
  ): ListFilterSource => ({ endpoint, load: () => readFacets().then(pick) });

  return defineListView<BillingDocumentRow>({
    endpoint,
    noun: "billing documents",
    title: "Billing",
    wide: true,
    columns: [
      {
        id: "document",
        header: "Document ID",
        kind: "title",
        cell: (r) => (
          <span className={`tabular-nums ${WRAP}`}>{r.documentNumber}</span>
        ),
      },
      {
        id: "name",
        header: "Document name",
        cell: (r) => <span className={WRAP}>{r.name}</span>,
      },
      {
        id: "kind",
        header: "Document kind",
        cell: (r) => billingKindLabel(r.kind),
      },
      {
        id: "related",
        header: "Related to",
        cell: (r) =>
          r.relatedTo ? (
            <Link
              to={`/surety/submissions/${encodeURIComponent(r.relatedTo.submissionId)}`}
              className="relative z-[1] whitespace-nowrap text-brk-accent hover:underline"
            >
              {r.relatedTo.sbNumber}
            </Link>
          ) : (
            <span className="whitespace-nowrap">Account</span>
          ),
      },
      {
        id: "added",
        header: "Added",
        cell: (r) => (
          <span className="tabular-nums">{formatBondDate(r.createdOn)}</span>
        ),
      },
      {
        id: "status",
        header: "Payment status",
        cell: (r) => {
          const status = paymentStatusDisplay(r.paymentStatus);
          return status === null ? (
            <span aria-label="No payment status">—</span>
          ) : (
            <span
              data-testid="payment-status"
              className={`${CHIP} ${TONE[status.tone]}`}
            >
              {status.label}
            </span>
          );
        },
      },
      {
        id: "download",
        header: "Download",
        cell: (r) => (
          <DocumentDownload
            label={`Download ${r.documentNumber}`}
            fallbackName={`${r.documentNumber}.pdf`}
            download={() =>
              downloadAccountDocument(fetchBlob, accountId, r.documentId)
            }
          />
        ),
      },
    ],
    rowKey: (r) => r.id,
    // An invoice has a page of its own; the other kinds do not.
    rowHref: (r) => (r.invoiceId === null ? null : invoiceHref(r.invoiceId)),
    rowLabel: (r) => `${billingKindLabel(r.kind)} ${r.documentNumber}`,
    search: {
      urlKey: "billQ",
      apiParam: "q",
      label: "Search billing documents",
      placeholder: "ID or document name",
    },
    filters: [
      {
        urlKey: "billYear",
        apiParam: "year",
        label: "Year",
        allLabel: "All years",
        options: served((f) => f.years.map((y) => ({ value: y, label: y }))),
      },
      {
        urlKey: "billKind",
        apiParam: "kind",
        label: "Document kind",
        allLabel: "All kinds",
        options: BILLING_KINDS.map((k) => ({
          value: k,
          label: billingKindLabel(k),
        })),
      },
      {
        urlKey: "billRelated",
        apiParam: "relatedTo",
        label: "Related to",
        allLabel: "Everything",
        options: served((f) => f.related),
      },
    ],
    pageKey: "billPage",
    pageSize: 10,
    totalNoun: "billing documents",
    empty: {
      title:
        "No billing documents yet — an invoice is created when Performance and Payment bonds are issued.",
    },
    load: async ({ filters, search, limit, offset }) => {
      const page = await listAccountBillingDocuments(request, accountId, {
        year: filters.year,
        kind: filters.kind,
        relatedTo: filters.relatedTo,
        q: search,
        limit,
        offset,
      });
      return {
        items: page.documents,
        total: page.total,
        limit: page.limit,
        offset: page.offset,
      };
    },
  });
}
